import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRecords, deleteRecord } from '../services/api';

const STATUS_MAP = {
  fixed:       { label: 'යථා තත්ත්වය',   css: 'bg-green-100 text-green-700'   },
  needs_parts: { label: 'කොටස් අවශ්‍ය', css: 'bg-yellow-100 text-yellow-700' },
  unusable:    { label: 'භාවිත නොහැක',   css: 'bg-red-100 text-red-700'       },
};

export default function MaintenanceListPage() {
  const { isSuperAdmin, isAdminOrAbove } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [filter,  setFilter]  = useState(''); // status filter

  const load = () => {
    setLoading(true);
    getRecords()
      .then(res => setRecords(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" — මෙම වාර්තාව මකා දැමීමට අවශ්‍යද?`)) return;
    try {
      await deleteRecord(id);
      load();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
  };

  const filtered = records.filter(r => {
    const matchSearch =
      r.school_number?.toLowerCase().includes(search.toLowerCase()) ||
      r.device_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.type?.toLowerCase().includes(search.toLowerCase()) ||
      r.school_assigned_number?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter ? r.status === filter : true;
    return matchSearch && matchFilter;
  });

 return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">නඩත්තු වාර්තා</h1>
          <p className="text-gray-500 text-sm mt-1">සම්පූර්ණ වාර්තා ලැයිස්තුව</p>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* New Record — admin and above */}
          {isAdminOrAbove && (
            <Link to="/maintenance/new"
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
              </svg>
              නව වාර්තාව
            </Link>
          )}

          {/* School Report Print */}
          <button
            onClick={() => {
              const school = prompt('පාසල් නම ඇතුළත් කරන්න:');
              if (!school) return;
              const date = prompt('දිනය ඇතුළත් කරන්න (YYYY-MM-DD):\nඋදා: 2026-04-05');
              if (!date) return;
              window.open(
                `/print/school-report?school=${encodeURIComponent(school)}&date=${encodeURIComponent(date)}`,
                '_blank'
              );
            }}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            🖨️ පාසල් වාර්තාව
          </button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="පාසල, උපකරණය, වර්ගය හෝ අංකය..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
        />
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500">
          <option value="">සියලු තත්ත්ව</option>
          <option value="fixed">යථා තත්ත්වය</option>
          <option value="needs_parts">කොටස් අවශ්‍ය</option>
          <option value="unusable">භාවිත නොහැක</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-20 text-gray-400">පූරණය වෙමින්...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-sm">{search || filter ? 'ගැලපෙන ප්‍රතිඵල නොමැත' : 'වාර්තා නොමැත'}</p>
            {!search && !filter && isAdminOrAbove && (
              <Link to="/maintenance/new" className="mt-3 inline-block text-red-500 hover:underline text-sm">
                නව වාර්තාවක් ඇතුළත් කරන්න →
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'පාසල', 'වර්ගය', 'උපකරණය', 'ශ්‍රේණිය', 'විවෘත දිනය', 'නිලධාරී', 'තත්ත්වය', 'ක්‍රියා'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r, i) => {
                  const s = STATUS_MAP[r.status] || { label: r.status, css: 'bg-gray-100 text-gray-600' };
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{r.school_number}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">{r.type?.replace('_', ' ') || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{r.device_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.school_assigned_number || '-'}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.opened_date?.slice(0, 10) || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.opening_officer_name || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.css}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex gap-3">
  {/* Print all records for this school+date - Type 1 */}
  <button
    onClick={() => window.open(
      `/print/status-report?school=${encodeURIComponent(r.school_number)}&date=${r.opened_date?.slice(0,10)}`,
      '_blank'
    )}
    className="text-gray-500 hover:text-gray-700 text-xs font-medium">
    වාර්තාව
  </button>
  {/* Print all records for this school+date - Type 3 */}
  <button
    onClick={() => window.open(
      `/print/detail-report?school=${encodeURIComponent(r.school_number)}&date=${r.opened_date?.slice(0,10)}`,
      '_blank'
    )}
    className="text-purple-500 hover:text-purple-700 text-xs font-medium">
    විස්තර
  </button>
  {/* Edit */}
  {isAdminOrAbove && (
    <Link to={`/maintenance/${r.id}/edit`}
      className="text-blue-600 hover:text-blue-800 text-xs font-medium">
      සංස්කරණය
    </Link>
  )}
  {/* Delete */}
  {isSuperAdmin && (
    <button
      onClick={() => handleDelete(r.id, r.school_number)}
      className="text-red-500 hover:text-red-700 text-xs font-medium">
      මකන්න
    </button>
  )}
</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
              මුළු වාර්තා: {filtered.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}